import axios, { type AxiosError } from 'axios'

import { config } from '@/core/config'
import type { ApiErrorResponse } from '@/types/interfaces/api'

/**
 * ★계약 영역 — 원본 up-smartorder-web/src/api/index.js의 동작을 그대로 유지.
 * 인증 헤더/토큰은 없다. 테넌트는 URL 파라미터로, LINE 인증은 주문 바디의
 * lineLiffAccessToken으로만 전달된다.
 */
export const client = axios.create({
  baseURL: config.api.smartOrder,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
  },
  /*
   * 원본에는 타임아웃이 없었다(axios 기본값도 무제한).
   * 결제 버튼은 요청 중 잠기므로 응답이 오지 않으면 버튼이 영구히 잠긴 채 아무 안내도 없다.
   * 실패로 처리해 사용자가 재시도할 수 있게 한다.
   */
  timeout: 30_000,
})

/** errorCode 없이 message만 온 에러를 감싼다 (원본은 평범한 Error를 throw했다) */
export class ApiError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

/** 사용자가 결제를 취소한 경우 — 조용히 무시된다 */
export class CancelledError extends Error {
  readonly isCancelled = true

  constructor(message = 'cancelled') {
    super(message)
    this.name = 'CancelledError'
  }
}

export function isCancelled(error: unknown): boolean {
  return (
    error instanceof CancelledError || (error as { isCancelled?: boolean })?.isCancelled === true
  )
}

/** errorCode가 붙은 axios 에러에서 코드를 꺼낸다 */
export function getErrorCode(error: unknown): number | string | undefined {
  const data = (error as AxiosError<ApiErrorResponse>)?.response?.data
  return data?.errorCode
}

/*
 * 원본 응답 인터셉터 로직 (변경 금지):
 *   errorCode 있음 → 원본 에러 그대로 reject (호출부가 코드로 분기)
 *   errorCode 없고 message 있음 → 서버 메시지로 Error throw
 *   그 외 → 원본 에러 reject
 */
client.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorResponse>) => {
    const data = error.response?.data
    if (data) {
      if (data.errorCode) {
        return Promise.reject(error)
      }
      throw new ApiError(data.message ?? 'Unknown error')
    }
    return Promise.reject(error)
  },
)
